import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import {
  Input,
  Dropdown,
  Text,
  getTheme,
  useMediaQuery,
  toast,
} from '@conexasaude/conexa-hero'
import useStyles from '../DataEditingForm/styles'
import { COUNTRY_STATES } from '@/utils/countryStates'
import { RenderIf } from '@/components/RenderIf'
import Icon from '@/components/RebrandedHeroIcons'
import { Controller, useFormContext } from 'react-hook-form'
import { getCep } from '@/modules/Checkout/checkout-services'
import { PersonalDataSectionType } from './types'
import { UserConfirmDataFormType } from '../ConfirmContactWrapper/types'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { INVALID_CEP_ADRESS_MESSAGE, INVALID_CEP_MESSAGE } from './constants'

const PersonalDataSection: FunctionComponent<PersonalDataSectionType> = ({
  sexes,
}) => {
  const styles = useStyles()
  const theme = getTheme()
  const { clinic, user } = useAuthContext()
  const isMobile = useMediaQuery(`(max-width: ${theme.grid.breakpoint.sm} )`)
  const [inputHidden, setInputHidden] = useState(false)
  const [cepLoading, setCepLoading] = useState(false)
  const [addressErrors, setAddressErrors] = useState(false)
  const { register, formState, control, reset, setValue, watch, resetField } =
    useFormContext<UserConfirmDataFormType>()
  const cepValue = watch('cep')
  const handleInvalidCep = () => {
    reset({
      streetName: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
    })
  }

  const searchCep = useCallback(
    async (cep: string) => {
      if (cep?.length < 9) return
      try {
        setCepLoading(true)
        const fullAddress = await getCep(cep)
        if (!fullAddress?.state) {
          setAddressErrors(true)
          resetAddressFields(true)
          handleInvalidCep()
          setAddressErrors(true)
          return
        }
        setAddressErrors(false)
        resetAddressFields()
        setValue('streetName', fullAddress?.address)
        setValue('neighborhood', fullAddress?.district)
        setValue('city', fullAddress?.city)
        setValue('state', fullAddress?.state?.toLocaleLowerCase())
      } catch (err) {
        setAddressErrors(true)
        const errorMessage =
          err?.response?.data?.object?.msg ||
          err?.response?.data?.msg ||
          err?.message

        toast({
          message: errorMessage,
          variant: 'error',
          autoCloseDuration: 5000,
          position: 'top-right',
        })
      } finally {
        setCepLoading(false)
        setValue('email', user.email)
      }
    },
    // eslint-disable-next-line
    [resetField, setValue, user.email],
  )

  const resetAddressFields = (resetNumber?: boolean) => {
    resetField('streetName')
    resetField('neighborhood')
    resetField('city')
    resetField('state')
    if (resetNumber) {
      resetField('number')
    }
  }

  useEffect(() => {
    if (cepValue !== user.address.formattedCep) {
      searchCep(cepValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cepValue, formState?.dirtyFields?.cep])

  const handleAddressRequired = (label: string) => {
    return clinic?.flag?.HabilitarObrigatoriedadeEndereco ? `${label}*` : label
  }

  return (
    <div>
      <div css={styles.sectionTitle}>
        <Text color={theme.colors.brand.primary[500]} variant="headingSmall">
          Dados pessoais
        </Text>
        <RenderIf condition={!isMobile}>
          <hr />
        </RenderIf>
        <RenderIf condition={isMobile}>
          <Icon
            onClick={() => setInputHidden(!inputHidden)}
            name={inputHidden && isMobile ? 'arrow-down' : 'arrow-up'}
            size="24px"
            color={theme.colors.brand[300]}
          />
        </RenderIf>
      </div>
      <RenderIf condition={!inputHidden || !isMobile}>
        <div css={styles.sectionGridContent}>
          <div css={styles.sectionGridCol}>
            <Input {...register('email')} label="E-mail" fullWidth disabled />
          </div>
          <div css={styles.sectionGridCol}>
            <Input
              {...register('cellphone')}
              error={formState?.errors?.cellphone?.message}
              label="Telefone celular*"
              placeholder={
                formState?.errors?.cellphone?.message ? '' : '(11) 99999-9999'
              }
              mask="(99) 99999-9999"
              fullWidth
            />
          </div>
          <div css={styles.sectionGridCol}>
            <Controller
              name="sex"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Dropdown
                  {...register('sex')}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  error={formState?.errors?.sex?.message}
                  label="Sexo*"
                  placeholder="Selecione seu sexo biológico"
                  fullWidth
                  data={sexes}
                />
              )}
            />
          </div>
          <div css={styles.sectionGridCol}>
            <Input
              {...register('cep')}
              icon={cepLoading && <Icon name={'loading'} />}
              error={
                addressErrors
                  ? addressErrors && INVALID_CEP_MESSAGE
                  : formState?.errors?.cep?.message
              }
              mask="99999-999"
              label={handleAddressRequired('CEP')}
              fullWidth
            />
          </div>
          <div css={styles.sectionGridCol}>
            <Input
              {...register('streetName')}
              error={
                addressErrors
                  ? addressErrors && INVALID_CEP_ADRESS_MESSAGE
                  : formState?.errors?.streetName?.message
              }
              label={handleAddressRequired('Rua')}
              fullWidth
            />
          </div>
          <div css={styles.sectionGridCol}>
            <Input
              {...register('number')}
              error={
                addressErrors
                  ? addressErrors && INVALID_CEP_ADRESS_MESSAGE
                  : formState?.errors?.number?.message
              }
              label={handleAddressRequired('Número')}
              fullWidth
              maxLength={10}
            />
          </div>
          <div css={styles.sectionGridCol}>
            <Input
              {...register('complement')}
              label="Complemento"
              fullWidth
              placeholder="Ex: Apartamento 20"
            />
          </div>
          <div css={styles.sectionGridCol}>
            <Input
              {...register('neighborhood')}
              error={
                addressErrors
                  ? addressErrors && INVALID_CEP_ADRESS_MESSAGE
                  : formState?.errors?.neighborhood?.message
              }
              label={handleAddressRequired('Bairro')}
              fullWidth
            />
          </div>
          <div css={styles.sectionGridCol}>
            <Input
              {...register('city')}
              error={
                addressErrors
                  ? addressErrors && INVALID_CEP_ADRESS_MESSAGE
                  : formState?.errors?.city?.message
              }
              label={handleAddressRequired('Cidade')}
              fullWidth
            />
          </div>
          <div css={styles.sectionGridCol}>
            <Controller
              name="state"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Dropdown
                  {...register('state')}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value.toLowerCase()}
                  error={
                    addressErrors
                      ? addressErrors && INVALID_CEP_ADRESS_MESSAGE
                      : formState?.errors?.state?.message
                  }
                  label={handleAddressRequired('Estado')}
                  fullWidth
                  data={COUNTRY_STATES}
                />
              )}
            />
          </div>
        </div>
      </RenderIf>
    </div>
  )
}

export { PersonalDataSection }
